import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  DatePicker,
  Typography,
  Space,
  message,
} from "antd";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import BonusService from "Services/EmployeeServices/BonusService";
import { BonusTypeOptions } from "Constants/Enums/BonusTypeEnum";

const { Text } = Typography;
const { TextArea } = Input;

const repName = (r) =>
  r.fullName ||
  r.name ||
  [r.firstName, r.lastName].filter(Boolean).join(" ") ||
  r.email ||
  "-";
const repId = (r) => r.id ?? r.employeeId ?? r.personalId;

// Manager-only screen: recommend a bonus for a direct report. The HR/Admin
// team then approves or rejects it (a recommendation is NOT yet approved/paid).
const RecommendBonus = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadReports = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(API.employee.getMyEmployees);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setReports(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load direct reports", e);
      message.error("Failed to load your team");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const openRecommend = (record) => {
    setSelected(record);
    form.resetFields();
  };

  const onSubmit = async (values) => {
    if (!selected) return;
    try {
      setSubmitting(true);
      const payload = {
        employeeId: repId(selected),
        bonusType: values.bonusType,
        amount: Number(values.amount),
        currency: values.currency?.trim() || "USD",
        awardDate: values.awardDate.format("YYYY-MM-DD"),
        reason: values.reason?.trim() || "",
      };
      await BonusService.recommend(payload);
      message.success("Bonus recommended. HR will review it.");
      setSelected(null);
      form.resetFields();
    } catch (e) {
      message.error(
        e.response?.data?.details || e.response?.data?.title || "Failed to recommend bonus"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "Employee", key: "name", render: (_, r) => repName(r) },
    {
      title: "Designation",
      key: "designation",
      render: (_, r) => r.designationName || r.designation || "-",
    },
    {
      title: "Department",
      key: "department",
      render: (_, r) => r.departmentName || r.department || "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Button type="link" onClick={() => openRecommend(r)}>
          Recommend Bonus
        </Button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card title="Recommend Bonus" className="card-professional" style={{ margin: 16 }}>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Recommend a bonus for one of your direct reports. HR reviews and approves it before it is
          included in payroll.
        </Text>
        <Table
          className="custom-table"
          rowKey={(r) => repId(r)}
          loading={loading}
          columns={columns}
          dataSource={reports}
          pagination={{ pageSize: 8 }}
          locale={{ emptyText: "You have no direct reports" }}
        />
      </Card>

      <Modal
        title={selected ? `Recommend bonus for ${repName(selected)}` : "Recommend bonus"}
        open={selected !== null}
        onCancel={() => setSelected(null)}
        onOk={() => form.submit()}
        okText="Recommend"
        okButtonProps={{ loading: submitting }}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="bonusType"
            label="Bonus Type"
            rules={[{ required: true, message: "Select a bonus type" }]}
          >
            <Select placeholder="Select type" options={BonusTypeOptions} />
          </Form.Item>
          <Space style={{ display: "flex" }} align="start">
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: "Enter the amount" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="currency" label="Currency" initialValue="USD">
              <Input placeholder="USD" />
            </Form.Item>
          </Space>
          <Form.Item
            name="awardDate"
            label="Award Date"
            rules={[{ required: true, message: "Select the award date" }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </DashboardLayout>
  );
};

export default RecommendBonus;
